import { AlertTriangle, Edit01, Map02, Modem02 } from "@untitledui/icons";
import {
	type MouseEvent,
	type ReactNode,
	type SyntheticEvent,
	useEffect,
	useMemo,
} from "react";

import { Badge } from "@/components/ui/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import type { LocationManagementState } from "../../../hooks/useLocationManagementState";
import type { ShadowZone } from "../../../types/location-map";

import {
	type RowActionItem,
	RowActionsMenu,
} from "../../../components/row-actions-menu";
import {
	getSensorsOutsideZone,
	getZoneBoundarySensorsOutsideAccordionTooltip,
} from "../../../utils/location/location-map-utils";
import { toTitleCase } from "../../../utils/shared/strings";

interface ZoneAccordionTitleProps {
	isActive?: boolean;
	isExpanded: boolean;
	mapState: LocationManagementState;
	onAddSensor?: () => void;
	onAddZone?: () => void;
	onToggle: () => void;
	sensorCount: number;
	titleClassName?: string;
	trailing?: ReactNode;
	zone: ShadowZone;
}

const stopAccordionToggle = (event: SyntheticEvent) => {
	event.stopPropagation();
};

export const ZoneAccordionTitle = ({
	isActive = false,
	isExpanded,
	mapState,
	onAddSensor,
	onAddZone,
	onToggle,
	sensorCount,
	titleClassName,
	trailing,
	zone,
}: ZoneAccordionTitleProps) => {
	const {
		cancelZoneEdit,
		canEditZoneDetails,
		editingSensorId,
		editingZoneId,
		isFloorEditActive,
		startZoneEdit,
		zoneEditDraft,
	} = mapState;

	const isEditingThisZone = editingZoneId === zone.id;
	const isZoneEditDisabled =
		!!editingSensorId ||
		isFloorEditActive ||
		(!!editingZoneId && !isEditingThisZone);
	const showZoneEditControls = isExpanded && canEditZoneDetails;
	const sensorCountLabel = `${sensorCount} ${sensorCount === 1 ? "sensor" : "sensors"}`;
	const sensorsOutsideCount = getSensorsOutsideZone(zone, {
		draft: zoneEditDraft,
		editingZoneId,
	}).length;
	const sensorsOutsideTooltip =
		getZoneBoundarySensorsOutsideAccordionTooltip(sensorsOutsideCount);

	const menuItems = useMemo((): RowActionItem[] => {
		if (!showZoneEditControls || isEditingThisZone) return [];
		return [
			{
				icon: Edit01,
				id: "edit-zone",
				isDisabled: isZoneEditDisabled,
				label: "Edit zone",
				onAction: () => startZoneEdit(zone),
			},
			onAddZone
				? {
						icon: Map02,
						id: "add-zone",
						isDisabled: isZoneEditDisabled,
						label: "Add zone",
						onAction: onAddZone,
					}
				: null,
			onAddSensor
				? {
						icon: Modem02,
						id: "add-sensor",
						isDisabled: isZoneEditDisabled,
						label: "Add sensor",
						onAction: onAddSensor,
					}
				: null,
		].filter(Boolean) as RowActionItem[];
	}, [
		isEditingThisZone,
		isZoneEditDisabled,
		onAddSensor,
		onAddZone,
		showZoneEditControls,
		startZoneEdit,
		zone,
	]);

	useEffect(() => {
		if (isEditingThisZone && !isExpanded) {
			cancelZoneEdit();
		}
	}, [cancelZoneEdit, isEditingThisZone, isExpanded]);

	const renderCountOrEditControls = () => {
		if (isEditingThisZone) {
			return (
				<span className="text-brand text-xs font-medium">Editing above</span>
			);
		}

		if (menuItems.length > 0) {
			return (
				<div
					onClick={(event: MouseEvent) => stopAccordionToggle(event)}
					onKeyDown={stopAccordionToggle}
					role="presentation"
				>
					<RowActionsMenu
						ariaLabel={`Actions for ${toTitleCase(zone.name)}`}
						isDisabled={isZoneEditDisabled}
						items={menuItems}
					/>
				</div>
			);
		}

		return (
			<Badge color="gray" size="sm" type="pill-color">
				{sensorCountLabel}
			</Badge>
		);
	};

	return (
		<div
			className={cx(
				"flex w-full items-center justify-between gap-3",
				titleClassName,
			)}
		>
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<Map02
					aria-hidden
					className={cx(
						"size-4 shrink-0",
						isActive ? "text-brand-secondary" : "text-fg-quaternary",
					)}
				/>
				<button
					aria-current={isActive ? "true" : undefined}
					aria-expanded={isExpanded}
					className="flex min-w-0 cursor-pointer flex-col rounded-md text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
					data-active={isActive ? "true" : "false"}
					onClick={onToggle}
					type="button"
				>
					<span className="text-[11px] leading-none font-medium tracking-wide text-tertiary uppercase">
						Zone
					</span>
					<span
						className={cx(
							"mt-0.5 truncate text-sm font-semibold",
							isActive ? "text-brand-secondary" : "text-primary",
						)}
					>
						{toTitleCase(zone.name)}
					</span>
				</button>
				{sensorsOutsideCount > 0 ? (
					<Tooltip placement="top" title={sensorsOutsideTooltip}>
						<TooltipTrigger
							aria-label={sensorsOutsideTooltip}
							onClick={stopAccordionToggle}
							onPointerDown={stopAccordionToggle}
						>
							<AlertTriangle className="size-4 shrink-0 text-utility-warning-500" />
						</TooltipTrigger>
					</Tooltip>
				) : null}
			</div>
			<div
				className="flex shrink-0 items-center gap-2"
				onClick={stopAccordionToggle}
				onKeyDown={stopAccordionToggle}
				role="presentation"
			>
				{trailing}
				{renderCountOrEditControls()}
			</div>
		</div>
	);
};
